/**
 * Phase 8 T-26 §29.F — Bulk-resumes intake body.
 *
 * Mounted inside the intake shell when `JdMode === 'bulk'`. Renders:
 *   - The same drag-drop zone as `<MultiJdList />`, but accepting
 *     `.pdf`, `.doc`, `.docx`, `.txt`, and `.zip` archives.
 *   - A scrollable parse-status list: filename + chip
 *     (`Queued` / `Parsing…` / `✓ Parsed` / `✕ Failed`) + `[×]` remove.
 *   - A Role/JD context textarea below the parse list (reuses the
 *     Single-JD textarea shape). If empty at submit, Stage 1 asks for
 *     one in the Clarification turn.
 *   - Scope pill is forced to "Scoring over: uploaded files only" in
 *     the intake footer — this body exposes that contract via the
 *     imperative handle; the parent wires the pill into the intake
 *     shell.
 *
 * Caps:
 *   - Max 100 files / 50 MB combined. Overflow triggers warning toast.
 *
 * Parsing strategy:
 *   - `.txt` / `.md`: inline via `FileReader#text()`.
 *   - `.pdf`: basic client-side via `pdfjs-dist` (matches the behaviour
 *     of `src/modules/smart-match/engine/documentParser.ts`).
 *   - `.zip`: unpacked with `jszip`; children are routed back through
 *     the parser recursively (no nested zips).
 *   - `.doc` / `.docx`: not parsed client-side here. We show `✕ Failed
 *     — server required` and leave `TODO(§9): server-side parse`.
 *
 * Only metadata (filename, size, parse status, and extracted text when
 * successful) is lifted up to the thread state. File bytes themselves
 * are NOT persisted to localStorage (§ spec guard).
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  FileUp,
  Loader2,
  X,
} from 'lucide-react';
import { useGlobalToast } from '@shared/components/Toast/ToastProvider';

export const MAX_BULK_FILES = 100;
export const MAX_BULK_BYTES = 50 * 1024 * 1024; // 50 MB

export type BulkParseStatus = 'queued' | 'parsing' | 'parsed' | 'failed';

export interface BulkResumeItem {
  id: string;
  name: string;
  sizeBytes: number;
  status: BulkParseStatus;
  /** Human-readable reason when `status === 'failed'`. */
  error?: string;
  /** Parsed text (not persisted to localStorage — kept in-memory only). */
  text?: string;
}

export interface BulkResumeUploadHandle {
  getItems: () => BulkResumeItem[];
  getContextJd: () => string;
}

interface BulkResumeUploadProps {
  initial?: BulkResumeItem[];
  initialContextJd?: string;
  onChange?: (items: BulkResumeItem[], contextJd: string) => void;
  handleRef?: React.MutableRefObject<BulkResumeUploadHandle | null>;
}

function newItemId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `br_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function extOf(name: string): string {
  return name.split('.').pop()?.toLowerCase() ?? '';
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

/** Parse a single file client-side per the §29.F strategy above. */
async function parseBulkFile(file: File): Promise<{ text?: string; error?: string }> {
  const ext = extOf(file.name);
  try {
    if (ext === 'txt' || ext === 'md' || file.type.startsWith('text/')) {
      const text = await file.text();
      return { text };
    }
    if (ext === 'pdf' || file.type === 'application/pdf') {
      const pdfjsLib = await import('pdfjs-dist');
      const workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).href;
      pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
      const buffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
      const pages: string[] = [];
      for (let i = 1; i <= pdf.numPages; i += 1) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        pages.push(
          content.items.map((it) => ('str' in it ? it.str : '')).join(' '),
        );
      }
      return { text: pages.join('\n\n').trim() };
    }
    if (ext === 'doc' || ext === 'docx') {
      // TODO(§9): server-side parse. Client-side .doc/.docx parsing via
      // officeparser / mammoth is intentionally not wired here — the
      // spec calls for a server round-trip so the extracted text lives
      // alongside the hiring-context audit log.
      return { error: 'Server required' };
    }
    return { error: `Unsupported: .${ext}` };
  } catch (err) {
    return { error: (err as Error).message || 'Parse failed' };
  }
}

/** Flatten a zip into plain Files (skipping directories + nested zips). */
async function unzip(file: File): Promise<File[]> {
  try {
    const { default: JSZip } = await import('jszip');
    const zip = await JSZip.loadAsync(file);
    const out: File[] = [];
    const entries = Object.values(zip.files);
    for (const entry of entries) {
      if (entry.dir) continue;
      if (extOf(entry.name) === 'zip') continue;
      const blob = await entry.async('blob');
      out.push(new File([blob], entry.name.split('/').pop() ?? entry.name));
    }
    return out;
  } catch {
    return [];
  }
}

export function BulkResumeUpload({
  initial = [],
  initialContextJd = '',
  onChange,
  handleRef,
}: BulkResumeUploadProps) {
  const { showToast } = useGlobalToast();
  const [items, setItems] = useState<BulkResumeItem[]>(initial);
  const [contextJd, setContextJd] = useState(initialContextJd);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Keep a ref of items in-sync so the async parse loop can compare
  // against the latest state without stale closures.
  const itemsRef = useRef<BulkResumeItem[]>(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    onChange?.(items, contextJd);
  }, [items, contextJd, onChange]);

  useEffect(() => {
    if (!handleRef) return;
    handleRef.current = {
      getItems: () => itemsRef.current,
      getContextJd: () => contextJd,
    };
    return () => {
      if (handleRef.current) handleRef.current = null;
    };
  }, [handleRef, contextJd]);

  const totalBytes = useMemo(
    () => items.reduce((acc, it) => acc + it.sizeBytes, 0),
    [items],
  );

  const warn = useCallback(
    (title: string, body?: string) => {
      showToast({ title, body, variant: 'warning' });
    },
    [showToast],
  );

  /** Enqueue parsing for one file. Writes status updates by id. */
  const enqueueParse = useCallback(async (id: string, file: File) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, status: 'parsing' } : it)),
    );
    const { text, error } = await parseBulkFile(file);
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? error
            ? {
                ...it,
                status: 'failed',
                error:
                  error === 'Server required'
                    ? 'Failed — server required'
                    : `Failed — ${error}`,
              }
            : { ...it, status: 'parsed', text }
          : it,
      ),
    );
  }, []);

  const addFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const incoming = Array.from(fileList);

      // Expand zips into their children.
      const expanded: File[] = [];
      for (const f of incoming) {
        if (extOf(f.name) === 'zip' || f.type === 'application/zip') {
          const children = await unzip(f);
          expanded.push(...children);
        } else {
          expanded.push(f);
        }
      }

      const currentCount = itemsRef.current.length;
      const currentBytes = itemsRef.current.reduce(
        (acc, it) => acc + it.sizeBytes,
        0,
      );
      let nextCount = currentCount;
      let nextBytes = currentBytes;
      const accepted: BulkResumeItem[] = [];
      const pendingParses: Array<{ id: string; file: File }> = [];
      let countOverflow = false;
      let bytesOverflow = false;

      for (const file of expanded) {
        if (nextCount >= MAX_BULK_FILES) {
          countOverflow = true;
          break;
        }
        if (nextBytes + file.size > MAX_BULK_BYTES) {
          bytesOverflow = true;
          break;
        }
        const id = newItemId();
        accepted.push({
          id,
          name: file.name,
          sizeBytes: file.size,
          status: 'queued',
        });
        pendingParses.push({ id, file });
        nextCount += 1;
        nextBytes += file.size;
      }

      if (accepted.length > 0) {
        setItems((prev) => [...prev, ...accepted]);
      }
      if (countOverflow) {
        warn(
          'Max 100 files per run.',
          'Some files were not added. Remove files before uploading more.',
        );
      } else if (bytesOverflow) {
        warn(
          'Max 50 MB per run.',
          'Some files were not added. The combined size would exceed 50 MB.',
        );
      }

      for (const { id, file } of pendingParses) {
        void enqueueParse(id, file);
      }
    },
    [enqueueParse, warn],
  );

  const retryParse = useCallback(
    (id: string) => {
      const item = itemsRef.current.find((it) => it.id === id);
      if (!item) return;
      // We don't hold the File object for retries — this is a stubbed
      // path. In a real implementation we'd keep a weak handle to the
      // File; for Phase 8 we just reset status to 'failed' with a
      // clearer message so the UX reads correctly from Stage 3.
      setItems((prev) =>
        prev.map((it) =>
          it.id === id
            ? {
                ...it,
                status: 'failed',
                error: 'Retry requires re-upload',
              }
            : it,
        ),
      );
    },
    [],
  );

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      if (!e.dataTransfer?.files) return;
      void addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const atFileCap = items.length >= MAX_BULK_FILES;
  const atByteCap = totalBytes >= MAX_BULK_BYTES;
  const metaLabel = `${items.length} / ${MAX_BULK_FILES} files · ${formatBytes(
    totalBytes,
  )} / 50 MB`;

  return (
    <div className="w-full max-w-[640px]">
      <div className="mb-3 flex items-center justify-between">
        <span
          className="text-[12px]"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          {metaLabel}
        </span>
        <span
          className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            color: 'var(--vscode-text-tertiary)',
            background: 'var(--vscode-bg-elevated)',
          }}
          title="Bulk-resume runs always score over the uploaded files only."
        >
          Scoring over: uploaded files only
        </span>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className="flex flex-col items-center justify-center gap-3 rounded-[12px] border-2 border-dashed px-6 py-8 text-center transition-colors"
        style={{
          width: '100%',
          maxWidth: 480,
          minHeight: 240,
          borderColor: dragging ? '#10B981' : 'var(--vscode-border-primary)',
          background: dragging ? 'var(--ggj-accent-soft,#D1FAE5)' : 'var(--vscode-bg-elevated)',
        }}
        role="region"
        aria-label="Bulk resume upload zone"
      >
        <FileText
          className="h-6 w-6"
          aria-hidden
          style={{ color: 'var(--vscode-text-tertiary)' }}
        />
        <p
          className="text-[13px]"
          style={{ color: 'var(--vscode-text-secondary)' }}
        >
          Drop résumés here, or click to select files.
          <br />
          Accepts .pdf, .doc, .docx, .txt, and .zip archives.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              if (atFileCap) {
                warn('Max 100 files per run.');
                return;
              }
              if (atByteCap) {
                warn('Max 50 MB per run.');
                return;
              }
              fileInputRef.current?.click();
            }}
            className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              color: 'var(--vscode-text-primary)',
            }}
          >
            <FileUp className="h-3.5 w-3.5" aria-hidden />
            Upload files
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.txt,.zip,application/pdf,application/zip,text/plain"
            className="hidden"
            onChange={(e) => {
              if (!e.target.files) return;
              void addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      {/* Parse-status list */}
      {items.length > 0 ? (
        <ul
          className="mt-4 flex max-h-[260px] flex-col gap-1.5 overflow-y-auto rounded-[10px] border p-2"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            background: 'var(--vscode-bg-elevated)',
          }}
          role="list"
          aria-label="Uploaded files parse status"
        >
          {items.map((it) => (
            <BulkRow
              key={it.id}
              item={it}
              onRemove={() => removeItem(it.id)}
              onRetry={() => retryParse(it.id)}
            />
          ))}
        </ul>
      ) : null}

      {/* Role/JD context textarea */}
      <div className="mt-4">
        <label
          htmlFor="ggj-bulk-context-jd"
          className="mb-1 block text-[11px] font-semibold uppercase tracking-wide"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          Role / JD context
        </label>
        <textarea
          id="ggj-bulk-context-jd"
          value={contextJd}
          onChange={(e) => setContextJd(e.target.value)}
          rows={4}
          placeholder="Paste or describe the role you want to score these résumés against. (Optional — the agent will ask if empty.)"
          className="min-h-[108px] w-full resize-y rounded-[10px] border bg-transparent p-3 text-[13px] outline-none focus:ring-1 focus:ring-[var(--ggj-accent,#10B981)]"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            color: 'var(--vscode-text-primary)',
            background: 'var(--vscode-bg-elevated)',
          }}
        />
      </div>
    </div>
  );
}

function BulkRow({
  item,
  onRemove,
  onRetry,
}: {
  item: BulkResumeItem;
  onRemove: () => void;
  onRetry: () => void;
}) {
  return (
    <li
      className="flex items-center gap-2 rounded-[8px] border px-2 py-1.5"
      style={{
        borderColor: 'var(--vscode-border-primary)',
      }}
    >
      <FileText
        className="h-3.5 w-3.5 shrink-0"
        aria-hidden
        style={{ color: 'var(--vscode-text-tertiary)' }}
      />
      <span
        className="min-w-0 flex-1 truncate text-[12px]"
        style={{ color: 'var(--vscode-text-primary)' }}
        title={item.name}
      >
        {item.name}
      </span>
      <span
        className="shrink-0 text-[11px]"
        style={{ color: 'var(--vscode-text-tertiary)' }}
      >
        {formatBytes(item.sizeBytes)}
      </span>
      <StatusChip status={item.status} error={item.error} />
      {item.status === 'failed' ? (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-[6px] border px-1.5 py-0.5 text-[10px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            color: 'var(--vscode-text-secondary)',
          }}
        >
          Retry
        </button>
      ) : null}
      <button
        type="button"
        onClick={onRemove}
        className="rounded p-0.5 transition-colors hover:bg-[var(--vscode-bg-hover)]"
        aria-label={`Remove ${item.name}`}
        style={{ color: 'var(--vscode-text-tertiary)' }}
      >
        <X className="h-3 w-3" aria-hidden />
      </button>
    </li>
  );
}

function StatusChip({
  status,
  error,
}: {
  status: BulkParseStatus;
  error?: string;
}) {
  if (status === 'queued') {
    return (
      <span
        className="inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]"
        style={{
          background: 'var(--vscode-bg-hover)',
          color: 'var(--vscode-text-tertiary)',
        }}
      >
        Queued
      </span>
    );
  }
  if (status === 'parsing') {
    return (
      <span
        className="inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]"
        style={{
          background: 'rgba(59,130,246,0.12)',
          color: 'rgb(29,78,216)',
        }}
      >
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        Parsing…
      </span>
    );
  }
  if (status === 'parsed') {
    return (
      <span
        className="inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]"
        style={{
          background: 'rgb(209 250 229)',
          color: 'rgb(6 95 70)',
        }}
      >
        <CheckCircle2 className="h-3 w-3" aria-hidden />
        Parsed
      </span>
    );
  }
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]"
      style={{
        background: 'rgba(239,68,68,0.12)',
        color: 'rgb(185,28,28)',
      }}
      title={error}
    >
      <AlertCircle className="h-3 w-3" aria-hidden />
      {error ?? 'Failed'}
    </span>
  );
}
